<template>
  <modal
    v-if="show"
    width="600px"
    height="700px"
    bodyOverflow="auto"
    :title="modalTitle"
    @close="checkClose"
    :read-article-url="`https://docs.bemob.com/${$i18n.locale}/landings-settings`"
  >
    <template v-if="step === 1">
      <div ref="scrollPanelWrapper" class="disabled">
        <div ref="scrollPanel" class="details">
          <div class="campaign-details__wrap">
            <div>
              <workspace-select
                :form="form"
                :labelHelpText="t('Workspace allows to make the required landing or other element accessible only by a specific member or group of members')"
                @change-workspace="onChangeWorkspace"
                :disabled="mode === 'edit' || disableWorkspaceSelect"
                :workspacesList="workspacesList"
                attach
              ></workspace-select>
            </div>
            <div
              v-bind="getIntroParams(2)"
            >
              <ui-input
                v-model="form.name"
                :placeholder="t('Name')"
                :hide-details="1"
                name="name"
                :label="t('Name')+'*'"
                :label-help="t('The first part of name is automatically generated basing on selected country.')"
                :error-messages="errorsDict['name']"
              />
              <p class="name-description">{{ nameDescription }}</p>
            </div>

            <div
              v-bind="getIntroParams(3)"
            >
              <ui-create-url-tags
                v-model="form.url"
                :chips="chips"
                :label="t('URL')+'*'"
                :showToolTip="false"
                :has-errors="!!errorsDict['url']"
              ></ui-create-url-tags>
            </div>

            <div>
              <ui-select
                v-model="form.countryId"
                :placeholder="t('Country')"
                :items="countryListPlusGlobal"
                item-text="name"
                item-value="id"
                :return-object="false"
                dialog-reposition-param="landing-dialog"
                :hide-details="2"
                :error-messages="errorsDict['countryId']"
              >
                <template #label>
                  <ui-label
                    :text="t('Country')"
                    :help-tooltip="t('Country selection is required to navigate easy through the offers list. This selection does not affect the traffic distribution.')"
                  ></ui-label>
                </template>
              </ui-select>
            </div>

            <div :class="{'plan-limits-warning__tags': showPlanLimitsWarning_tags}">
              <ui-select
                planLimits="tagsLandings"
                v-model="form.tags"
                :placeholder="t('Select or type tags...')"
                :label="t('Tags')"
                :label-help="t('Add personalized tags to search and navigate easy through landings list.')"
                :items="store.cache.tags.landings.data"
                item-text="name"
                dialog-reposition-param="landing-dialog"
                :hide-details="2"
                combobox
                multi-line
                multiple
                chips
                :error-messages="errorsDict['tags']"
              >
                <template #no-data>
                  <no-tag-found></no-tag-found>
                </template>
              </ui-select>
              <button v-if="showPlanLimitsWarning_tags" class="plan-limits-warning__clear" @click="clearTags">
                {{t('Reset tags')}}
              </button>
              <ui-tooltip-icon
                class="plan-limits-warning__tooltip"
                preset="error"
                v-if="showPlanLimitsWarning_tags"
                :text="t('This option is no longer active due to the subscription change to the plan which doesn’t include this option or this add-on is switched off in your subscriptions settings.')"
              ></ui-tooltip-icon>
            </div>

            <div>
              <ui-input
                v-model="form.offers"
                :placeholder="t('Offers')"
                :hide-details="2"
                :disabled="!!landingId"
                name="offers"
                :error-messages="errorsDict['offers']"
                :label="t('Offers')"
                :label-help="t('Set this to the number of offers that are present on this landing page. If you want to rotate offers, set value to 1 and add several offers in campaign`s flow settings.')"
              />
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <section class="justify-space-between xs12 align-items-center" style="display: flex; width: 100%" v-if="step === 1">
        <div>
          <error-aggregator
            v-model="errors"
            v-model:active="errorsControlActive"
          ></error-aggregator>
        </div>
        <div class="d-flex justify-end align-center">
          <ui-update-info v-if="mode === 'edit'" :data="form"></ui-update-info>
          <ui-button
            :text="t('Cancel')"
            icon="fas fa-times"
            @click="checkClose"
          ></ui-button>

          <ui-button
            button-style="accent"
            icon="fas fa-check"
            :text="t('Save')"
            :disabled="isActionsDisabled"
            v-bind="getIntroParams(4)"
            @click="submit(); tutorial.close();"
          ></ui-button>
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import WorkspaceSelect from '@/views/reports/pickers/wokrspace-select.vue';
import NoTagFound from '@/views/reports/views/no-tag-found.vue';
import { computed } from 'vue';
import {
  useLandingDialog,
  type LandingDialogData,
} from './useLandingDialog';

const props = withDefaults(defineProps<{
  data?: LandingDialogData;
}>(), {
  data: () => ({}),
});

const emit = defineEmits<{
  close: [];
}>();

const mode = computed(() => props.data.mode ?? 'create');

const {
  store,

  t,
  show,
  step,
  form,
  landingId,
  disableWorkspaceSelect,
  errors,
  errorsControlActive,
  errorsDict,
  modalTitle,
  chips,
  countryListPlusGlobal,
  nameDescription,
  workspacesList,
  isActionsDisabled,
  showPlanLimitsWarning_tags,
  scrollPanel,
  scrollPanelWrapper,
  tutorial,
  onChangeWorkspace,
  checkClose,
  clearTags,
  submit,
  getIntroParams,
} = useLandingDialog(mode.value, () => props.data, () => emit('close'));
</script>

<style lang="scss" scoped>
@import '@/styles/dialogs/report-modals';
@import '@/styles/dialogs/landing-dialog';

.name-description {
  margin-left: 16px;
  word-break: break-all;
  font-style: italic;
  font-size: 12px;
}

.update-info {
  display: inline-block;
  white-space: pre-line;
  word-break: break-all;
  max-width: 272px;
  margin-left: 15px;
  margin-right: 15px;

  :deep(.name) {
    max-width: 250px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: pre-wrap;
  }

  :deep(div) {
    display: inline-block;
  }
}

@media (max-width: 900px) {
  .update-info {
    display: none;
  }
}
</style>
